/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import Text from "../ui/Text";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import FormatDateTime from "../formatters/FormatDateTime";
import BookingStepItem from "../booking/steps/BookingStepItem";
import { ROUTES, RECURRING_PROFILE_STATUS_COLOR } from "../../constants/constants";
import { formatRecurringPeriod } from "../../utils/helpers/membership";
import { getRecurringProfileDates, getRecurringProfileLeadDateKey } from "../../utils/helpers/recurringProfile";
import FormatPrice from "../formatters/FormatPrice";
import { useTranslate } from "../../hooks/useTranslate";
import Flex from "../ui/Flex";
import Stack from "../ui/Stack";

const StyledCard = styled(BookingStepItem)`
  padding: 1rem;
`;

const Highlight = styled(Flex)`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.05);

  ${({ theme }) => theme.containerMedia.sm} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HighlightTextLabel = styled(Text)`
  text-transform: uppercase;
`;

const DateColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  ${({ theme }) => theme.containerMedia.sm} {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const DateValue = ({ value }) => <FormatDateTime value={value} format={FormatDateTime.FORMAT.DATE} />;

const ClientRecurringProfileItem = ({ profile }) => {
  const { _t } = useTranslate();
  const { membership } = profile;
  const period = formatRecurringPeriod(membership?.duration, membership?.duration_type, _t);

  const dates = getRecurringProfileDates(profile);
  const leadKey = getRecurringProfileLeadDateKey(profile);
  const lead = dates.find((date) => date.labelKey === leadKey);
  const columns = dates.filter((date) => date !== lead);

  return (
    <StyledCard>
      <Stack $gap="1.5rem">
        <Flex $align="center" $justify="space-between" $wrap="wrap" $gap="0.5rem">
          <Flex $align="center" $gap="0.5rem">
            <Badge
              title={`recurring_profile_status_${profile.status}`}
              color={RECURRING_PROFILE_STATUS_COLOR[profile.status]}
            />
            <Text color="dark" weight="bold">
              {profile.payment_processor}
            </Text>
          </Flex>
          <Button as={Link} to={ROUTES.CLIENT_RECURRING_PROFILE.replace(":id", profile.id)} variant="outline" size="m" title="View profile" />
        </Flex>

        <DateColumns>
          {columns.map(({ labelKey, value }) => (
            <Stack key={labelKey} $gap="0.125rem">
              <Text size="small">{labelKey}</Text>
              <Text color="dark" weight="bold" size="small">
                <DateValue value={value} />
              </Text>
            </Stack>
          ))}
        </DateColumns>

        {(lead || membership?.recurring_price) && (
          <Highlight $gap="0.5rem" $align="center" $wrap="wrap" $justify="space-between">
            {lead && (
              <Stack $gap="0.125rem">
                <HighlightTextLabel size="small" weight="medium">{lead.labelKey}</HighlightTextLabel>
                <Text color="dark" weight="bold">
                  <DateValue value={lead.value} />
                </Text>
              </Stack>
            )}
            {membership?.recurring_price && (
              <Flex $align="baseline" $gap="0.25rem">
                <Text color="dark" weight="bold">
                  <FormatPrice value={membership.recurring_price} currency={membership.currency} />
                </Text>
                {period && (
                  <Text>
                    {` / ${period}`}
                  </Text>
                )}
              </Flex>
            )}
          </Highlight>
        )}
      </Stack>
    </StyledCard>
  );
};

export default ClientRecurringProfileItem;
